import { useUserStore } from '@/stores/user'
import { useUserMetricsStore } from '@/stores/userMetrics'
import { useAppStore } from '@/stores/app'
import {
  CUSTOM_PROMPTS_LIMITS,
  CUSTOM_PROMPT_CATEGORIES,
  MAX_FREE_CUSTOM_PROMPTS_TOTAL,
} from '@/constants/resourceLimits'

const CREATION_METHOD_TO_CATEGORY = {
  EDITED_DAAI: 'editedDaai',
  FREE_TEXT: 'freeText',
  AI_GENERATED: 'aiGenerated',
}

const mapCreationMethodToCategory = (creationMethod) => {
  if (!creationMethod) return null
  return CREATION_METHOD_TO_CATEGORY[String(creationMethod).toUpperCase()] ?? null
}

/**
 * Centraliza contagem e limites de modelos customizados (métricas + lista da API).
 * Plano Free: até 1 modelo por categoria (editado DAAI, texto livre, IA) = 3 no total.
 */
export function useCustomPromptLimits() {
  const userStore = useUserStore()
  const userMetricsStore = useUserMetricsStore()
  const appStore = useAppStore()

  const { limits: userPlanLimits, isProUser } = storeToRefs(userStore)
  const { userMetrics } = storeToRefs(userMetricsStore)
  const { customPromptList } = storeToRefs(appStore)

  const perTypeLimit = computed(() => {
    return (
      userPlanLimits.value?.customPromptsPerType ??
      CUSTOM_PROMPTS_LIMITS.editedDaai
    )
  })

  const maxTotalCustomPrompts = computed(() => MAX_FREE_CUSTOM_PROMPTS_TOTAL)

  const getCategoryLimit = (category) => {
    return CUSTOM_PROMPTS_LIMITS[category] ?? perTypeLimit.value
  }

  const metricsCategoryCounts = computed(() => ({
    editedDaai:
      userMetrics.value?.customPrompts?.editedDaai?.totalInLifetime ?? 0,
    freeText: userMetrics.value?.customPrompts?.freeText?.totalInLifetime ?? 0,
    aiGenerated:
      userMetrics.value?.customPrompts?.aiGenerated?.totalInLifetime ?? 0,
  }))

  const listCategoryCounts = computed(() => {
    const counts = {
      editedDaai: 0,
      freeText: 0,
      aiGenerated: 0,
    }

    for (const item of customPromptList.value?.items ?? []) {
      const category = mapCreationMethodToCategory(item.creationMethod)
      if (category) counts[category] += 1
    }

    return counts
  })

  const categoryCounts = computed(() => {
    return CUSTOM_PROMPT_CATEGORIES.reduce((acc, category) => {
      acc[category] = Math.max(
        metricsCategoryCounts.value[category] ?? 0,
        listCategoryCounts.value[category] ?? 0,
      )
      return acc
    }, {})
  })

  const totalCustomPromptsCount = computed(() => {
    return CUSTOM_PROMPT_CATEGORIES.reduce(
      (sum, category) => sum + (categoryCounts.value[category] ?? 0),
      0,
    )
  })

  const isCategoryLimitReached = (category) => {
    if (!category || isProUser.value) return false
    return categoryCounts.value[category] >= getCategoryLimit(category)
  }

  const exceededByCategory = computed(() => {
    return CUSTOM_PROMPT_CATEGORIES.reduce((acc, category) => {
      acc[category] = isCategoryLimitReached(category)
      return acc
    }, {})
  })

  const isAnyCategoryLimitReached = computed(() => {
    return CUSTOM_PROMPT_CATEGORIES.some((category) =>
      isCategoryLimitReached(category),
    )
  })

  const isAllCategoriesLimitReached = computed(() => {
    return CUSTOM_PROMPT_CATEGORIES.every((category) =>
      isCategoryLimitReached(category),
    )
  })

  const isTotalCustomPromptsLimitReached = computed(() => {
    if (isProUser.value) return false
    return isAllCategoriesLimitReached.value
  })

  const canCreateNewCustomPrompt = computed(() => {
    if (isProUser.value) return true
    return CUSTOM_PROMPT_CATEGORIES.some(
      (category) => !isCategoryLimitReached(category),
    )
  })

  const canCreateInCategory = (category) => {
    if (isProUser.value) return true
    if (!category) return canCreateNewCustomPrompt.value
    return !isCategoryLimitReached(category)
  }

  return {
    perTypeLimit,
    maxTotalCustomPrompts,
    totalCustomPromptsCount,
    categoryCounts,
    exceededByCategory,
    isCategoryLimitReached,
    isAnyCategoryLimitReached,
    isAllCategoriesLimitReached,
    isTotalCustomPromptsLimitReached,
    canCreateNewCustomPrompt,
    canCreateInCategory,
  }
}
